import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap, Beaker, Search } from "lucide-react";

export default function Onboarding() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [families, setFamilies] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("UGC_NET");

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const api = makeApi(getToken);
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => toast.error("Failed to load subjects"));
    api.get("/me").then((r) => {
      if (r.data?.onboarded) navigate("/app", { replace: true });
    });
  }, [isLoaded, isSignedIn, getToken, navigate]);

  const toggleFamily = (f) => {
    setFamilies((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  };

  const toggleSubject = (sid) => {
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(sid)) n.delete(sid);
      else n.add(sid);
      return n;
    });
  };

  const filtered = useMemo(() => {
    const list = subjects.filter((s) => s.family === tab);
    if (!q.trim()) return list;
    const t = q.toLowerCase();
    return list.filter((s) => s.name.toLowerCase().includes(t) || s.code.includes(t));
  }, [subjects, tab, q]);

  useEffect(() => {
    // when families change, set the tab to first family
    if (families.length && !families.includes(tab)) setTab(families[0]);
  }, [families, tab]);

  const handleSave = async () => {
    if (!selected.size) {
      toast.error("Select at least one subject");
      return;
    }
    setSaving(true);
    try {
      const api = makeApi(getToken);
      await api.post("/me/onboarding", {
        families,
        subjects: Array.from(selected),
      });
      toast.success("Welcome! Your dashboard is ready.");
      navigate("/app", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center p-4 sm:p-8">
      <Card className="w-full max-w-4xl p-6 sm:p-10 border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Step {step} of 2</p>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-1">
              {step === 1 ? "Which exam are you preparing for?" : "Pick the subjects you care about"}
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              {step === 1
                ? "Choose one or both. We'll only show you what matters."
                : "You can select across both exams. You can change this later without losing progress."}
            </p>
          </div>
        </div>

        {step === 1 && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <FamilyCard
                active={families.includes("UGC_NET")}
                onClick={() => toggleFamily("UGC_NET")}
                icon={GraduationCap}
                tone="blue"
                title="UGC NET"
                subtitle="87 subjects · Paper I + II"
                testid="onboarding-family-ugc"
              />
              <FamilyCard
                active={families.includes("CSIR_NET")}
                onClick={() => toggleFamily("CSIR_NET")}
                icon={Beaker}
                tone="teal"
                title="CSIR NET"
                subtitle="5 subjects · Part A + B + C"
                testid="onboarding-family-csir"
              />
            </div>
            <div className="flex justify-end mt-8">
              <Button
                onClick={() => setStep(2)}
                disabled={!families.length}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="onboarding-next-btn"
              >
                Continue
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-100">
                {families.includes("UGC_NET") && (
                  <TabsTrigger value="UGC_NET" data-testid="onboarding-tab-ugc">UGC NET</TabsTrigger>
                )}
                {families.includes("CSIR_NET") && (
                  <TabsTrigger value="CSIR_NET" data-testid="onboarding-tab-csir">CSIR NET</TabsTrigger>
                )}
              </TabsList>

              {["UGC_NET", "CSIR_NET"].map((fam) =>
                families.includes(fam) ? (
                  <TabsContent key={fam} value={fam} className="mt-5">
                    <div className="relative mb-4">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Search subject by code or name"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9"
                        data-testid="onboarding-search"
                      />
                    </div>
                    <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {filtered.map((s) => {
                        const sid = `${s.family}:${s.code}`;
                        const checked = selected.has(sid);
                        return (
                          <label
                            key={sid}
                            htmlFor={`sub-${sid}`}
                            className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${
                              checked ? "bg-blue-50/60" : ""
                            }`}
                            data-testid={`onboarding-subject-${s.family}-${s.code}`}
                          >
                            <Checkbox
                              id={`sub-${sid}`}
                              checked={checked}
                              onCheckedChange={() => toggleSubject(sid)}
                            />
                            <span className="font-mono text-xs text-slate-500 w-10">{s.code}</span>
                            <span className="text-sm text-slate-900">{s.name}</span>
                          </label>
                        );
                      })}
                      {!filtered.length && (
                        <div className="px-4 py-10 text-center text-sm text-slate-500">No subjects match.</div>
                      )}
                    </div>
                  </TabsContent>
                ) : null,
              )}
            </Tabs>

            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="onboarding-back-btn">
                Back
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600" data-testid="onboarding-selected-count">
                  {selected.size} selected
                </span>
                <Button
                  onClick={handleSave}
                  disabled={saving || !selected.size}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="onboarding-save-btn"
                >
                  {saving ? "Saving…" : "Save & continue"}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function FamilyCard({ active, onClick, icon: Icon, tone, title, subtitle, testid }) {
  const tones = {
    blue: { bg: "bg-blue-50", fg: "text-blue-600" },
    teal: { bg: "bg-teal-50", fg: "text-teal-600" },
  };
  const t = tones[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-6 rounded-xl border transition-all ${
        active
          ? "border-blue-500 bg-blue-50/40 ring-2 ring-blue-500/20"
          : "border-slate-200 bg-white hover:border-blue-300"
      }`}
    >
      <div className={`h-11 w-11 rounded-lg ${t.bg} ${t.fg} grid place-items-center`}>
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-display font-semibold text-lg mt-4">{title}</h3>
      <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
    </button>
  );
}
